import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Button, Card, Field, Notice } from '@ailab/ui';
import { authenticatedResponseSchema, loginResponseSchema } from '@ailab/contracts';
import { useRefreshMe } from '../../lib/account';
import { apiRequest, errorMessage, rememberCsrf } from '../../lib/http';
import styles from './account.module.css';

export function LoginPage() {
  const navigate = useNavigate();
  const refreshMe = useRefreshMe();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'credentials' | 'mfa'>('credentials');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  async function finish() {
    await refreshMe();
    await navigate({ to: '/cuenta' });
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      if (step === 'credentials') {
        const result = await apiRequest('/auth/login', loginResponseSchema, {
          method: 'POST',
          body: { email, password },
        });
        rememberCsrf(result.csrfToken);
        if (result.status === 'mfa_required') setStep('mfa');
        else await finish();
      } else {
        const result = await apiRequest('/auth/login/mfa', authenticatedResponseSchema, {
          method: 'POST',
          body: { code },
        });
        rememberCsrf(result.csrfToken);
        await finish();
      }
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.narrow}>
      <h1>Entrar</h1>
      <p className={styles.lead}>Exclusivo para miembros de AiLendra Labs.</p>
      <Card>
        <form className={styles.form} onSubmit={submit} noValidate>
          {step === 'credentials' ? (
            <>
              <Field
                label="Email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Field
                label="Contraseña"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </>
          ) : (
            <Field
              label="Código de verificación"
              hint="Escribe los seis dígitos de tu aplicación de autenticación."
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          )}
          {error ? <Notice tone="error">{error}</Notice> : null}
          <Button type="submit" busy={busy}>
            {step === 'credentials' ? 'Entrar' : 'Verificar y entrar'}
          </Button>
        </form>
        <div className={styles.links}>
          <Link to="/registro">Crear cuenta</Link>
          <Link to="/recuperar">¿Has olvidado tu contraseña?</Link>
        </div>
      </Card>
    </div>
  );
}
